/**
 * @summary - Component that Provides Asset Condition Form Page
 * @author Purushothaman T
 * @since 08-07-2024
 */
import { Box, Stack } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useEffect, useState } from "react";
import { DefaultCard } from "../../assets/styled-components/card";
import { assetcondition } from "../../utility/constants/masters";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import CloseButton from "../commoncomponents/CloseButton";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
} from "../../utility/tokenUtility";
import axios from "axios";
import { FemsGraph, BemsGraph } from "../../utility/apiURLS";
import { useNavigate } from "react-router-dom";
import CreateNewBtn from "../commoncomponents/CreateNewBtn";
import utils from "../../utility/utilities";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

function AssetConditionList() {
  const column = assetcondition.gridColumn;
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const serviceOptions = [
    { label: "FEMS", value: "FEMS", id: 1 },
    { label: "BEMS", value: "BEMS", id: 2 },
  ];
  const [service, setService] = useState(serviceOptions[0]);

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM013A");

  useEffect(() => {
    fetchData(service);
  }, []);
  const fetchData = async (service) => {
    setRowData([]);
    setIsLoading(true);
    try {
      const token = getAuthToken("id_token");
      const facilityId = getFacilityId("facilityId");
      const companyId = getcompanyId("companyId");
      const config = {
        headers: {
          // Attach bearer token to request header
          Authorization: `Bearer ${token}`,
        },
      };

      let response;
      if (service.label == "FEMS") {
        response = await axios.get(
          `${FemsGraph}/api/AssetConditionStatusAssetNo/GetFemAssetConditionAssetNoFetch/${facilityId}`,
          config
        );
      } else {
        response = await axios.get(
          `${BemsGraph}/api/AssetConditionStatusAssetNo/GetBemAssetConditionAssetNoFetch/${facilityId}`,
          config
        );
      }

      setRowData(
        response.data.map((data) => {
          const conditionStatus = data.assetStatusConditionVMs;
          return {
            ...data,
            service: service.label,
            date: utils.formatDate({
              dateString:
                conditionStatus[conditionStatus.length - 1]?.statusdate,
              format: "dd/mm/yyyy",
              dbdate: true,
            }),
            equipmentcondition:
              conditionStatus[conditionStatus.length - 1]?.conditionstatus,
          };
        }),
      );
    } catch (error) {
      console.error("Error fetching options:", error);
      // Handle error (e.g., show error message)
    } finally {
      setIsLoading(false);
    }
  };
  const handleCreateNew = () => {
    navigate("/assetcondition/form", {
      state: {
        service: service.label,
      }
    });
  };
  const handleServiceChange = (newValue) => {
    setService(newValue);
    if (newValue.label !== service.label) {
      fetchData(newValue);
    }
  };

  const colDefs = [
    { field: column.assetno.field, headerName: column.assetno.header },
    { field: column.assetdesc.field, headerName: column.assetdesc.header },
    { field: column.typecode.field, headerName: column.typecode.header },
    {
      field: column.typecodedesc.field,
      headerName: column.typecodedesc.header,
      wrapHeaderText: true,
    },
    { field: column.date.field, headerName: column.date.header },
    {
      field: column.equipmentcondition.field,
      headerName: column.equipmentcondition.header,
      wrapHeaderText: true,
    },
  ];
  const handleEdit = (data) => {
    navigate("/assetcondition/form", {
      state: {
        responseData: {
          ...data,
          label: data.assetName,
          value: data.assetName,
          id: data.assetId,
        },

        isEditMode: true,
      },
    });
  };
  const handleView = (data) => {
    navigate("/assetcondition/form", {
      state: {
        responseData: {
          ...data,
          label: data.assetName,
          value: data.assetName,
          id: data.assetId,
        },

        isViewMode: true,
      },
    });
  };

  return (
    <>
      <PageTitle title={"Asset Condition"} />
      <DefaultCard>
        <Stack mb={1} direction={"row"} justifyContent={"space-between"}>
          <SectionTitle title={"Asset Condition List"} />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomSelectInput
              fullWidth={false}
              labelSize={"14px"}
              sx={{ width: 150 }}
              label={"Service"}
              options={serviceOptions}
              value={service}
              onChange={handleServiceChange}
              placeholder={"Select Service"}
              highlight={true}
              disableClearable={true}
            />
            {access?.canCreate && <CreateNewBtn onClick={handleCreateNew} />}
            <RefreshExportButtons
              pagination={false}
              exportFunction={() => { }}
              handleRefresh={() => fetchData(service)}
              exportData={rowData}
              refData={gridApi}
              title={`Asset Condition`}
            />
          </Stack>
        </Stack>
        <Box>
          <CustomAgGrid
            rows={rowData}
            columns={colDefs}
            refSet={gridApi}
            setGridApi={setGridApi}
            handleEdit={access?.canEdit &&handleEdit}
            handleView={access?.canView &&handleView}
            isLoading={isLoading}
          />
        </Box>
      </DefaultCard>
      <CloseButton />
    </>
  );
}

export default AssetConditionList;
